// Msg 412 UAT #4 — human-readable explanation of WHY an assignment sits at
// its current progress/status. Percentage alone is not operationally useful.
//
// P0 UAT 2026-08-11 — state PRECEDENCE across lifecycle dimensions: a later
// report / client-decision / process state must override the earlier
// assignment status (never "informe en preparación" after delivery/closure).
// Callers may pass the extra dimensions on the row itself (report_status,
// client_outcome, process_closed — provided by GET /admin/assignments) or
// via the `extra` argument.
const OUTCOME_ES = {
  selected: 'Seleccionado',
  not_selected: 'No seleccionado',
  on_hold: 'En espera',
  cancelled: 'Cancelado',
};
const OUTCOME_EN = {
  selected: 'Selected',
  not_selected: 'Not selected',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
};

export function clientOutcomeLabel(outcome, isES) {
  if (!outcome) return null;
  const map = isES ? OUTCOME_ES : OUTCOME_EN;
  // Safe fallback for unknown/legacy values: humanize the raw key.
  return map[outcome] || outcome.toString().replace(/_/g, ' ');
}

export function assignmentStateReason(a, isES, extra = {}) {
  if (!a) return null;
  const progress = a.progress || 0;
  const reportStatus = extra.reportStatus ?? a.report_status;
  const clientOutcome = extra.clientOutcome ?? a.client_outcome;
  const processClosed = extra.processClosed ?? a.process_closed;

  // ---- Late lifecycle dimensions take precedence over assignment status ----
  if (['submitted', 'under_review', 'completed', 'released'].includes(a.status)) {
    if (reportStatus === 'released_to_client') {
      const parts = [isES ? 'Informe entregado al cliente' : 'Report delivered to the client'];
      const outcomeLabel = clientOutcomeLabel(clientOutcome, isES);
      if (outcomeLabel) {
        parts.push((isES ? 'Decisión del cliente: ' : 'Client decision: ') + outcomeLabel);
      }
      if (processClosed) {
        parts.push(isES ? 'Proceso cerrado' : 'Process closed');
      } else if (!outcomeLabel) {
        parts.push(isES ? 'En espera de la decisión del cliente' : 'Awaiting the client decision');
      }
      return parts.join(' — ');
    }
    if (reportStatus === 'approved') {
      return isES
        ? 'Informe aprobado — pendiente de entrega al cliente'
        : 'Report approved — pending delivery to the client';
    }
    if (['under_review', 'edited_by_hm', 'draft_ai_generated'].includes(reportStatus)) {
      return isES
        ? 'Respuestas enviadas — informe en revisión del consultor'
        : 'Responses submitted — report under consultant review';
    }
  }

  const deadlinePassed = a.deadline && new Date(a.deadline) < new Date()
    && !['submitted', 'under_review', 'completed', 'released'].includes(a.status);
  switch (a.status) {
    case 'draft':
      return isES
        ? 'Borrador — aún no se ha enviado la invitación al evaluado'
        : 'Draft — invitation not yet sent to the employee';
    case 'requested':
      return deadlinePassed
        ? (isES
          ? 'Fecha límite vencida — el evaluado nunca inició el cuestionario'
          : 'Deadline expired — the employee never started the questionnaire')
        : (isES
          ? 'Invitación enviada — esperando que el evaluado inicie'
          : 'Invitation sent — waiting for the employee to start');
    case 'in_progress':
      if (deadlinePassed) {
        return isES
          ? `Fecha límite vencida — cuestionario respondido al ${progress}%`
          : `Deadline expired — questionnaire ${progress}% answered`;
      }
      return isES
        ? `Esperando al evaluado — cuestionario respondido al ${progress}% y guardado como avance parcial`
        : `Waiting for the employee — questionnaire ${progress}% answered, saved as partial progress`;
    case 'submitted':
      return isES
        ? 'Respuestas enviadas — informe en preparación'
        : 'Responses submitted — report in preparation';
    case 'under_review':
      return isES
        ? 'Pendiente de revisión del consultor CTH'
        : 'Pending HM Advisor review';
    case 'completed':
      return isES ? 'Evaluación completada' : 'Evaluation completed';
    case 'released':
      return isES ? 'Informe liberado al cliente' : 'Report released to the client';
    case 'cancelled':
      return isES ? 'Evaluación cancelada' : 'Evaluation cancelled';
    case 'paused':
      return isES ? 'Evaluación en pausa' : 'Evaluation paused';
    default:
      return null;
  }
}
